/**
 * 表格列国际化工具
 * 用于根据当前语言动态获取表格列名
 */

// 支持的语言列表及其对应的列名字段后缀
const LANG_SUFFIX_MAP = {
    'en': '_en',
    'zh-TW': '_zh_tw',
    'ja': '_ja',
    'ko': '_ko',
    'ar': '_ar',
    'ms': '_ms',
    'fr': '_fr',
    'de': '_de',
    'ru': '_ru',
    'vi': '_vi',
    'th': '_th',
    'hi': '_hi'
};

/**
 * 根据当前语言获取列名
 * @param {Object} column - 列配置对象
 * @param {String} lang - 语言代码 (zh-CN, en, zh-TW, ja, ko, ar, ms, fr, de, ru, vi, th, hi)
 * @returns {String} 对应语言的列名
 */
export function getColumnTitle(column, lang = 'zh-CN') {
    if (!column) return '';
    
    // 中文简体为默认值
    if (lang === 'zh-CN') {
        return column.column_name;
    }
    
    // 根据后缀查找对应语言的列名，找不到则回退到默认列名
    const suffix = LANG_SUFFIX_MAP[lang];
    if (suffix) {
        return column['column_name' + suffix] || column.column_name;
    }
    
    return column.column_name;
}

/**
 * 批量转换列配置的列名为当前语言
 * @param {Array} columns - 列配置数组
 * @param {String} lang - 语言代码
 * @returns {Array} 转换后的列配置数组
 */
export function translateColumns(columns, lang = 'zh-CN') {
    if (!columns || !Array.isArray(columns)) {
        return [];
    }
    
    return columns.map(column => ({
        ...column,
        column_name: getColumnTitle(column, lang)
    }));
}

/**
 * 监听语言变化并更新列名
 * @param {Vue} vm - Vue 实例
 * @param {Array} originalColumns - 原始列配置（包含所有语言）
 * @param {String} columnsKey - 存储列配置的data属性名
 */
export function watchLanguageForColumns(vm, originalColumns, columnsKey = 'rkKeyList') {
    if (!vm || !originalColumns) return;
    
    // 创建计算属性或方法来获取当前语言的列
    const updateColumns = () => {
        const currentLang = vm.$i18n ? vm.$i18n.locale : localStorage.getItem('language') || 'zh-CN';
        const translatedColumns = translateColumns(originalColumns, currentLang);
        
        // 更新组件中的列配置
        if (vm[columnsKey]) {
            vm[columnsKey] = translatedColumns;
        }
    };
    
    // 初始更新
    updateColumns();
    
    // 如果使用了 vue-i18n，监听语言变化
    if (vm.$i18n) {
        vm.$watch(
            () => vm.$i18n.locale,
            () => {
                updateColumns();
            }
        );
    }
}

/**
 * 根据语言获取对应的列名（通用版）
 * 支持任意基础字段名 + 语言后缀的命名方式
 * @param {Object} item - 数据对象
 * @param {String} lang - 语言代码 (zh-CN, en, zh-TW, ja, ko, ar, ms, fr, de, ru, vi, th, hi)
 * @param {String} baseField - 基础字段名，如 'title' 或 'item_name'
 * @returns {String} 对应语言的字段值
 */
export function getColumnTitleByLang(item, lang = 'zh-CN', baseField = 'title') {
    if (!item) return '';
    
    // 中文简体为默认值
    if (lang === 'zh-CN') {
        return item[baseField];
    }
    
    // 根据后缀查找对应语言的字段，找不到则回退到默认字段
    const suffix = LANG_SUFFIX_MAP[lang];
    if (suffix) {
        return item[baseField + suffix] || item[baseField];
    }
    
    return item[baseField];
}

export default {
    getColumnTitle,
    translateColumns,
    watchLanguageForColumns,
    getColumnTitleByLang
};
